import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { PageContainer } from '@/components/shared/PageContainer';
import { Plus, Settings, Loader2, Edit2 } from 'lucide-react';
import { useComplianceItemTypes, useCreateItemType, useUpdateItemType } from '../hooks';
import type { CreateItemTypeInput, ComplianceItemType } from '../types';

type ItemCategory = 'legal' | 'health' | 'safety' | 'professional' | 'custom';

const CATEGORIES: { value: ItemCategory; label: string }[] = [
    { value: 'legal', label: 'Legal' },
    { value: 'health', label: 'Health' },
    { value: 'safety', label: 'Safety' },
    { value: 'professional', label: 'Professional' },
    { value: 'custom', label: 'Custom' },
];

const CATEGORY_COLORS: Record<ItemCategory, string> = {
    legal: 'bg-blue-100 text-blue-800',
    health: 'bg-emerald-100 text-emerald-800',
    safety: 'bg-amber-100 text-amber-800',
    professional: 'bg-purple-100 text-purple-800',
    custom: 'bg-gray-100 text-gray-800',
};

export default function ComplianceItemTypesPage() {
    const [open, setOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<ComplianceItemType | null>(null);
    const [name, setName] = useState('');
    const [category, setCategory] = useState<ItemCategory>('legal');
    const [hasExpiry, setHasExpiry] = useState(false);
    const [validityMonths, setValidityMonths] = useState('');
    const [docRequired, setDocRequired] = useState(false);

    const { data: types = [], isLoading } = useComplianceItemTypes(false);
    const createItemType = useCreateItemType();
    const updateItemType = useUpdateItemType();

    useEffect(() => {
        if (editTarget) {
            setName(editTarget.name);
            setCategory((editTarget.category as ItemCategory) || 'legal');
            setHasExpiry(editTarget.has_expiry);
            setValidityMonths(editTarget.default_validity_months ? String(editTarget.default_validity_months) : '');
            setDocRequired(editTarget.document_required);
            setOpen(true);
        }
    }, [editTarget]);

    function handleCreateOrUpdate() {
        if (!name.trim()) return;

        const input: CreateItemTypeInput = {
            name: name.trim(),
            category,
            has_expiry: hasExpiry,
            default_validity_months: hasExpiry && validityMonths ? parseInt(validityMonths) : undefined,
            document_required: docRequired,
            target_type: editTarget ? editTarget.target_type : 'all',
        };

        if (editTarget) {
            updateItemType.mutate(
                { id: editTarget.id, data: input },
                {
                    onSuccess: () => {
                        resetForm();
                        setOpen(false);
                    },
                }
            );
        } else {
            createItemType.mutate(input, {
                onSuccess: () => {
                    resetForm();
                    setOpen(false);
                },
            });
        }
    }

    function resetForm() {
        setEditTarget(null);
        setName('');
        setCategory('legal');
        setHasExpiry(false);
        setValidityMonths('');
        setDocRequired(false);
    }

    function toggleActive(id: string, currentlyActive: boolean) {
        updateItemType.mutate({
            id,
            data: { is_active: !currentlyActive },
        });
    }

    if (isLoading) {
        return (
            <PageContainer title="Compliance Item Types">
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer
            title="Compliance Item Types"
            description="Define the compliance requirements for your organisation"
            actions={
                <Dialog open={open} onOpenChange={(val) => {
                    setOpen(val);
                    if (!val) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button onClick={() => setEditTarget(null)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Item Type
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>{editTarget ? 'Edit Compliance Item Type' : 'Create Compliance Item Type'}</DialogTitle>
                            <p className="text-sm text-muted-foreground">
                                {editTarget ? 'Update the details for this compliance tracking type.' : 'Define a compliance requirement to track across employees.'}
                            </p>
                        </DialogHeader>
                        <div className="space-y-5 pt-2">
                            {/* Name */}
                            <div className="space-y-2">
                                <Label htmlFor="type-name">Name</Label>
                                <Input
                                    id="type-name"
                                    placeholder="e.g. DBS Check, First Aid Certificate"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>

                            {/* Category */}
                            <div className="space-y-2">
                                <Label>Category</Label>
                                <Select value={category} onValueChange={(v) => setCategory(v as ItemCategory)}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CATEGORIES.map((cat) => (
                                            <SelectItem key={cat.value} value={cat.value}>
                                                {cat.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Has expiry checkbox */}
                            <div className="rounded-lg border p-4 space-y-4">
                                <div className="flex items-start gap-3">
                                    <input
                                        id="has-expiry"
                                        type="checkbox"
                                        checked={hasExpiry}
                                        onChange={(e) => setHasExpiry(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300"
                                    />
                                    <div>
                                        <label htmlFor="has-expiry" className="text-sm font-medium cursor-pointer">
                                            Has expiry date
                                        </label>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            Enable if this item expires and needs renewing
                                        </p>
                                    </div>
                                </div>

                                {hasExpiry && (
                                    <div className="space-y-2 pt-2 border-t">
                                        <Label htmlFor="validity">Default validity period (months)</Label>
                                        <Input
                                            id="validity"
                                            type="number"
                                            placeholder="e.g. 36 for a 3-year DBS check"
                                            value={validityMonths}
                                            onChange={(e) => setValidityMonths(e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Document required checkbox */}
                            <div className="rounded-lg border p-4">
                                <label htmlFor="doc-required" className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        id="doc-required"
                                        type="checkbox"
                                        checked={docRequired}
                                        onChange={(e) => setDocRequired(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300"
                                    />
                                    <div>
                                        <span className="text-sm font-medium">Document upload required</span>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            Employees must upload evidence (e.g. scanned certificate, PDF)
                                        </p>
                                    </div>
                                </label>
                            </div>

                            {/* Submit */}
                            <Button
                                className="w-full"
                                onClick={handleCreateOrUpdate}
                                disabled={!name.trim() || createItemType.isPending || updateItemType.isPending}
                            >
                                {createItemType.isPending || updateItemType.isPending ? 'Saving...' : (editTarget ? 'Save Changes' : 'Create Item Type')}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            }
        >
            {types.length === 0 ? (
                <Card>
                    <CardContent className="py-16 text-center">
                        <Settings className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-semibold mb-2">No item types configured</h3>
                        <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
                            Compliance item types define what your organisation tracks per employee —
                            things like DBS checks, right-to-work documents, health declarations, and
                            professional registrations.
                        </p>
                        <Button onClick={() => { setEditTarget(null); setOpen(true); }}>
                            <Plus className="h-4 w-4 mr-2" />
                            Create Your First Item Type
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-2">
                    {types.map((t) => (
                        <Card key={t.id} className={!t.is_active ? 'opacity-50' : ''}>
                            <CardContent className="py-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Badge className={CATEGORY_COLORS[t.category as ItemCategory] ?? ''}>
                                            {t.category}
                                        </Badge>
                                        <span className="font-medium">{t.name}</span>
                                        {t.has_expiry && (
                                            <span className="text-xs text-muted-foreground">
                                                {t.default_validity_months
                                                    ? `${t.default_validity_months}mo validity`
                                                    : 'has expiry'}
                                            </span>
                                        )}
                                        {t.document_required && (
                                            <Badge variant="outline" className="text-xs">Doc required</Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                            onClick={() => setEditTarget(t)}
                                        >
                                            <Edit2 className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => toggleActive(t.id, t.is_active)}
                                            className={t.is_active ? 'text-amber-600 hover:text-amber-700' : 'text-emerald-600 hover:text-emerald-700'}
                                        >
                                            {t.is_active ? 'Deactivate' : 'Activate'}
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </PageContainer>
    );
}